import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ANNIE_STUDIO, FONT_SIZE, INK } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

export enum AnnieStudioStatus {
	Loading = "loading",
	Error = "error",
	Ready = "ready",
}

type Props = {
	status: AnnieStudioStatus;
	onRetry: () => void;
	stage: ReactNode;
	controls: ReactNode;
	compact: boolean;
};

/** The Studio body: a loader or retry, then stage beside controls (desktop) or pinned above them (phone). */
export const AnnieStudioBody: FC<Props> = ({ status, onRetry, stage, controls, compact }) => {
	const theme = useTheme();
	const { t } = useTranslation();

	if (status !== AnnieStudioStatus.Ready) {
		return (
			<Box
				data-testid="annie-studio-status"
				sx={{ display: "grid", placeItems: "center", alignContent: "center", gap: 1.5, minHeight: ANNIE_STUDIO.STAGE_AVATAR, color: INK.MUTED }}
			>
				{status === AnnieStudioStatus.Error ? (
					<>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS }}>{t("annie.studio.load_error")}</Typography>
						<SystemButton buttonVariant="secondary" size="compact" noFullWidthMobile onClick={onRetry} data-testid="annie-studio-retry">
							{t("annie.studio.retry")}
						</SystemButton>
					</>
				) : (
					<JumpingDots />
				)}
			</Box>
		);
	}
	if (compact) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Box sx={{ position: "sticky", top: 0, zIndex: 1, pt: 1.5, pb: 1, bgcolor: "background.paper" }}>{stage}</Box>
				{controls}
			</Box>
		);
	}
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: `${ANNIE_STUDIO.STAGE_WIDTH}px minmax(0, 1fr)`, gap: 3, height: ANNIE_STUDIO.BODY_HEIGHT }}>
			{stage}
			<Box sx={{ minHeight: 0, overflowY: "auto", paddingInlineEnd: theme.spacing(1) }}>{controls}</Box>
		</Box>
	);
};
